/**
 * IdentityExtractDetailModal — full IdentityProviderExtract review surface.
 *
 * Renders the ~80-field parsed LexisNexis report that landed in
 * identity.IdentityProviderExtract + child tables when the FSR posted the
 * HTML via /v3/identity/import-lexisnexis-html. Surfaces every signal the
 * LnHtmlParser captured so the FSR can make the officer-decision call
 * (Approve / ManualReview / Decline / Override).
 *
 * Triggered from IndividualAmlInsightSection's "View full report" button
 * when person.amlInsightExtractId is set.
 *
 * Bill 2026-05-18 (BACKLOG-0068 Tier 1 completion). Brand-styled inline
 * (matches the section-component convention).
 */

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type {
  GetExtractOutcome,
  IdentityProviderExtractRow,
  IdentityProviderVerifDetailRow,
  IdentityProviderRedFlagRow,
  IdentityProviderFraudWarningRow,
  IdentitySupportingDocumentRow,
} from '@retail-ops/api-client';
import { useConfig } from '@/contexts/ConfigContext';
import { cipAllVerified, cipVerification, fraudIndexReview, lnDiscrepancies } from '../shared/lnDiscrepancies';
import { availableFollowUps, followUpToolOf, fraudPointFloors, showVerificationRiskIndex, type LnFollowUpTool } from '../shared/lnDisplaySettings';

// ─────────────────────────────────────────────────────────────────────────
// PCB brand roles -> theme tokens (styles/tokens.css; light values mirror
// ~/.claude/skills/pcb-brand/SKILL.md, dark + high contrast follow Teams)
// ─────────────────────────────────────────────────────────────────────────
const BRAND = {
  primary:    'var(--pcb-blue)',
  primaryFill: 'var(--brand-fill)',
  primaryHv:  'var(--brand-fill-hover)',
  dark:       'var(--pcb-blue-dark)',
  body:       'var(--ink-800)',
  caption:    'var(--ink-500)',
  border:     'var(--surface-border)',
  bgCode:     'var(--surface-subtle)',
  bgInfo:     'var(--info-bg)',
  bgSuccess:  'var(--ok-bg)',
  bgWarn:     'var(--warn-bg)',
  bgError:    'var(--err-bg)',
  green:      'var(--ok-text)',
  red:        'var(--err-text)',
  amber:      'var(--warn-text)',
};

const ST: Record<string, CSSProperties> = {
  section: {
    marginBottom: 20,
  },
  h3: {
    fontSize: 16,
    fontWeight: 500,
    color: BRAND.primary,
    margin: '0 0 8px',
    paddingBottom: 4,
    borderBottom: `1px solid ${BRAND.border}`,
  },
  kv: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, max-content) 1fr',
    gap: '6px 14px',
    fontSize: 13,
  },
  kvLabel: { color: BRAND.caption, fontWeight: 500 },
  kvValue: { color: BRAND.body, fontFamily: 'ui-monospace, Consolas, monospace', wordBreak: 'break-word' },
  table: {
    width: '100%',
    fontSize: 13,
    borderCollapse: 'collapse' as const,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 4,
    overflow: 'hidden' as const,
  },
  th: {
    background: BRAND.primaryFill,
    color: 'var(--on-brand)',
    fontWeight: 500,
    padding: '6px 10px',
    textAlign: 'left' as const,
  },
  td: { padding: '6px 10px', borderTop: `1px solid ${BRAND.border}`, verticalAlign: 'top' as const },
  bigNumber: {
    fontSize: 32,
    fontWeight: 700,
    color: BRAND.dark,
    fontFamily: 'ui-monospace, Consolas, monospace',
    lineHeight: 1,
  },
};

function pillStyle(bg: string, fg: string): CSSProperties {
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    background: bg,
    color: fg,
    fontSize: 12,
    fontWeight: 600,
  };
}

function riskIndexTier(ri: number | null | undefined): { label: string; bg: string; fg: string } {
  if (ri == null) return { label: '—', bg: BRAND.bgCode, fg: BRAND.caption };
  // LN's scale: 0=High Risk, 50=Low Risk.
  if (ri >= 40) return { label: 'Low risk',      bg: BRAND.bgSuccess, fg: BRAND.green };
  if (ri >= 20) return { label: 'Moderate risk', bg: BRAND.bgWarn,    fg: 'var(--warn-text)' };
  return            { label: 'High risk',     bg: BRAND.bgError,   fg: BRAND.red };
}

function fraudPointTier(
  fp: number | null | undefined,
  floors: { low: number; moderate: number } = { low: 750, moderate: 500 },
): { label: string; bg: string; fg: string } {
  if (fp == null) return { label: '—', bg: BRAND.bgCode, fg: BRAND.caption };
  // FraudPoint scale: 300=highest risk, 999=lowest risk (BIS354 p.14). Cut-offs are RM-owned.
  if (fp >= floors.low) return { label: 'Low risk',      bg: BRAND.bgSuccess, fg: BRAND.green };
  if (fp >= floors.moderate) return { label: 'Moderate risk', bg: BRAND.bgWarn,    fg: 'var(--warn-text)' };
  return            { label: 'High risk',     bg: BRAND.bgError,   fg: BRAND.red };
}

function tryParseJsonArray<T>(json: string | null | undefined): T[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function fmtMaybe(v: string | number | boolean | null | undefined): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

export interface IdentityExtractDetailModalProps {
  open: boolean;
  extractId: string | undefined;
  onClose: () => void;
  /**
   * D3 (Bill 2026-06-03): origination channel. The InstantID red-flags / risk
   * indicators (incl. "Vulnerable Victim") are the ONLINE-applicant fraud
   * screen — they do NOT gate an in-branch, in-person, photo-ID + visual-match
   * opening. Drives the advisory banner. Defaults 'In-Branch' (ROP-teams = FSR).
   */
  channel?: 'In-Branch' | 'Online';
}

export function IdentityExtractDetailModal(props: IdentityExtractDetailModalProps): JSX.Element | null {
  const { open, extractId, onClose, channel = 'In-Branch' } = props;
  const api = useApiClient();
  const [data, setData] = useState<GetExtractOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !extractId) return;
    setLoading(true);
    setError(null);
    api.retail.identity
      .getExtract(extractId)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError((err as Error)?.message ?? 'Failed to load extract.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, extractId, api]);

  // CHG-0141 (Bill 2026-11-02): the same report as a PDF laid out like the LexisNexis website report.
  const downloadPdf = async (): Promise<void> => {
    if (!extractId) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const blob = await api.retail.identity.getExtractReportPdf(extractId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Identity Verification.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err: unknown) {
      setPdfError((err as Error)?.message ?? 'Could not print the report.');
    } finally {
      setPdfBusy(false);
    }
  };

  if (!open) return null;

  return (
    <ModalShell
      open
      size="lg"
      title="LexisNexis Identity Report — full extract"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
          {pdfError && <span style={{ color: BRAND.red, fontSize: 12 }}>{pdfError}</span>}
          <Button variant="ghost" onClick={() => void downloadPdf()} disabled={!data || pdfBusy}>
            {pdfBusy ? 'Preparing PDF…' : 'Download PDF'}
          </Button>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {loading && <div style={{ padding: 32, textAlign: 'center', color: BRAND.caption }}>Loading…</div>}
      {error && (
        <div style={{ padding: 12, background: BRAND.bgError, color: BRAND.red, border: `1px solid ${BRAND.red}`, borderRadius: 4 }}>
          {error}
        </div>
      )}
      {!loading && !error && data && (
        <Body extract={data.extract} verifDetails={data.verifDetails} redFlags={data.redFlags} fraudWarnings={data.fraudWarnings} channel={channel} />
      )}
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Body — broken into per-section renderers so each can be hidden when empty
// ─────────────────────────────────────────────────────────────────────────

interface BodyProps {
  extract: IdentityProviderExtractRow;
  verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow>;
  redFlags: ReadonlyArray<IdentityProviderRedFlagRow>;
  fraudWarnings: ReadonlyArray<IdentityProviderFraudWarningRow>;
  channel: 'In-Branch' | 'Online';
}

function Body(props: BodyProps): JSX.Element {
  const { extract, verifDetails, redFlags, fraudWarnings, channel } = props;

  return (
    <div style={{ display: 'grid', gap: 8, fontSize: 13, color: BRAND.body }}>
      <DecisioningAdvisoryBanner channel={channel} />
      <CipSection extract={extract} verifDetails={verifDetails} />
      <SupportingDocumentsSection extract={extract} verifDetails={verifDetails} />
      <DiscrepanciesSection extract={extract} verifDetails={verifDetails} redFlags={redFlags} fraudWarnings={fraudWarnings} />
      <HeaderSection extract={extract} />
      <ScoresSection extract={extract} />
      <VerifSummarySection extract={extract} />
      <RiskIndicatorsSection extract={extract} />
      <WatchlistSection extract={extract} />
      <VerificationDetailSection rows={verifDetails} />
      <RedFlagsSection rows={redFlags} />
      <FraudWarningsSection rows={fraudWarnings} />
      <SupplementalSection extract={extract} />
      <IpAddressSection extract={extract} />
      <FollowUpSection extract={extract} redFlags={redFlags} fraudWarnings={fraudWarnings} />
      <PermissibleUseSection extract={extract} />
      <AuditSection extract={extract} />
    </div>
  );
}

// D3 (Bill 2026-06-03) — channel-aware decisioning banner. The FraudPoint Score
// is the gate; everything below is advisory. For in-branch in-person openings
// the InstantID red-flags / risk indicators (esp. "Vulnerable Victim") do NOT
// apply — they're the online-applicant fraud screen. Per Online Applicants
// procedure pp.2-3 (project_fraudpoint_gating_d2_procedure_20260603).
function DecisioningAdvisoryBanner({ channel }: { channel: 'In-Branch' | 'Online' }): JSX.Element {
  const inBranch = channel !== 'Online';
  return (
    <section
      style={{
        ...ST.section,
        background: BRAND.bgInfo,
        border: '1px solid var(--pcb-blue)',
      }}
    >
      <h3 style={ST.h3}>How to read this report</h3>
      <p style={{ margin: 0, fontSize: 12, color: BRAND.body }}>
        The <strong>FraudPoint Score</strong> is the decisioning value (750+ proceed ·
        500–749 follow-up · 300–499 decline). The risk indicators, red flags, and fraud
        warnings below are <strong>advisory context</strong>, not gating criteria.
        {inBranch && (
          <> For this <strong>in-branch, in-person</strong> opening (photo ID + visual match),
          the InstantID red flags — including <strong>“Vulnerable Victim”</strong> — do NOT
          apply; they are the online-applicant fraud screen.</>
        )}
      </p>
    </section>
  );
}

function HeaderSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element {
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Report</h3>
      <div style={ST.kv}>
        <div style={ST.kvLabel}>Title</div>
        <div style={ST.kvValue}>{fmtMaybe(extract.lnReportTitle)}</div>
        <div style={ST.kvLabel}>Provider</div>
        <div style={ST.kvValue}>{extract.sourceId} ({extract.sourceMode})</div>
        <div style={ST.kvLabel}>Ingested</div>
        <div style={ST.kvValue}>{fmtMaybe(extract.ingestedUtc)} {extract.ingestedByOfficerCode ? `by ${extract.ingestedByOfficerCode}` : ''}</div>
        <div style={ST.kvLabel}>Parser</div>
        <div style={ST.kvValue}>{extract.parserVersion}</div>
      </div>
    </section>
  );
}

function ScoresSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element {
  const { config } = useConfig();
  const settings = config.systemSettings as Record<string, unknown> | undefined;
  // Risk Management decides whether FSRs see the Verification Risk Index - it does not gate.
  const showRi = showVerificationRiskIndex(settings);
  const ri = extract.verificationRiskIndex;
  const fp = extract.fraudPointScore;
  const riTier = riskIndexTier(ri);
  const fpTier = fraudPointTier(fp, fraudPointFloors(settings));
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>{showRi ? 'Headline scores' : 'FraudPoint score'}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: showRi ? '1fr 1fr' : '1fr', gap: 12 }}>
        {showRi && (
        <div style={{ padding: 14, border: `1px solid ${BRAND.border}`, borderRadius: 6, background: 'var(--surface-card)' }}>
          <div style={{ fontSize: 11, color: BRAND.caption, textTransform: 'uppercase', letterSpacing: 0.5 }}>Verification Risk Index</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
            <span style={ST.bigNumber}>{ri ?? '—'}</span>
            <span style={{ fontSize: 12, color: BRAND.caption }}>/ 50</span>
            <span style={pillStyle(riTier.bg, riTier.fg)}>{riTier.label}</span>
          </div>
          <div style={{ fontSize: 12, color: BRAND.caption, marginTop: 4 }}>0 = highest risk · 50 = lowest risk</div>
          {extract.verificationRiskLevelText && (
            <div style={{ fontSize: 12, color: BRAND.body, marginTop: 8 }}>{extract.verificationRiskLevelText}</div>
          )}
        </div>
        )}
        <div style={{ padding: 14, border: `1px solid ${BRAND.border}`, borderRadius: 6, background: 'var(--surface-card)' }}>
          <div style={{ fontSize: 11, color: BRAND.caption, textTransform: 'uppercase', letterSpacing: 0.5 }}>FraudPoint Score</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
            <span style={ST.bigNumber}>{fp ?? '—'}</span>
            <span style={{ fontSize: 12, color: BRAND.caption }}>300 – 999</span>
            <span style={pillStyle(fpTier.bg, fpTier.fg)}>{fpTier.label}</span>
          </div>
          <div style={{ fontSize: 12, color: BRAND.caption, marginTop: 4 }}>999 = lowest risk · 300 = highest risk</div>
          <div style={{ marginTop: 8 }}>
            <FraudPointIndices extract={extract} />
          </div>
        </div>
      </div>
    </section>
  );
}

function FraudPointIndices({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element {
  const indices: Array<[string, number | null | undefined]> = [
    ['Synthetic',         extract.riskIndex_Synthetic],
    ['Stolen',            extract.riskIndex_Stolen],
    ['Manipulated',       extract.riskIndex_Manipulated],
    ['Vulnerable Victim', extract.riskIndex_VulnerableVictim],
    ['Friendly Fraud',    extract.riskIndex_FriendlyFraud],
    ['Suspicious Act.',   extract.riskIndex_SuspiciousActivity],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 11 }}>
      {indices.map(([label, value]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: BRAND.caption }}>{label}</span>
          <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', color: BRAND.body, fontWeight: 600 }}>
            {value ?? '—'}
            {value != null && <span style={{ color: BRAND.caption, fontWeight: 400 }}>/9</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function VerifSummarySection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element | null {
  const a = extract.verifSummary_NameAddrPhone;
  const b = extract.verifSummary_NameAddrSsn;
  const c = extract.verifSummary_DobMatchLevel;
  if (!a && !b && !c) return null;
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Verification summary</h3>
      <table style={ST.table}>
        <tbody>
          <tr><td style={{ ...ST.td, width: 220, fontWeight: 500 }}>Name + Address + Phone</td><td style={ST.td}>{fmtMaybe(a)}</td></tr>
          <tr><td style={{ ...ST.td, fontWeight: 500 }}>Name + Address + SSN</td><td style={ST.td}>{fmtMaybe(b)}</td></tr>
          <tr><td style={{ ...ST.td, fontWeight: 500 }}>DOB Match Level</td><td style={ST.td}>{fmtMaybe(c)}</td></tr>
        </tbody>
      </table>
    </section>
  );
}

function RiskIndicatorsSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element | null {
  const items = tryParseJsonArray<string>(extract.potentialRiskIndicatorsJson);
  if (items.length === 0) return null;
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Potential risk indicators ({items.length})</h3>
      <ul style={{ margin: 0, paddingLeft: 22, color: BRAND.body }}>
        {items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
      </ul>
    </section>
  );
}

interface WatchlistHit { message?: string; sourceList?: string; matchName?: string; matchScore?: string }
function WatchlistSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element | null {
  const hits = tryParseJsonArray<WatchlistHit>(extract.watchlistResultsJson);
  // (Bill 2026-06-03) — when the report HAS a #watchlist section but no hits,
  // that's a CLEAN result. Surface it explicitly ("No OFAC or Watchlist Matches")
  // so the FSR sees the screen was checked & clear, not silently omitted.
  if (hits.length === 0) {
    const checked = !!extract.watchlistRawHtml && extract.watchlistRawHtml.trim().length > 0;
    if (!checked) return null;
    return (
      <section style={ST.section}>
        <h3 style={ST.h3}>OFAC / Watchlist</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: BRAND.bgSuccess, borderLeft: `4px solid ${BRAND.green}`, borderRadius: 4, fontSize: 12, color: BRAND.body, fontWeight: 500 }}>
          <span style={{ color: BRAND.green, fontWeight: 700 }}>✓</span>
          No OFAC or Watchlist Matches
        </div>
      </section>
    );
  }
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Watchlist hits ({hits.length})</h3>
      <table style={ST.table}>
        <thead>
          <tr><th style={ST.th}>Match name</th><th style={ST.th}>Source list</th><th style={ST.th}>Score</th><th style={ST.th}>Detail</th></tr>
        </thead>
        <tbody>
          {hits.map((h, i) => (
            <tr key={i}>
              <td style={ST.td}>{fmtMaybe(h.matchName)}</td>
              <td style={ST.td}>{fmtMaybe(h.sourceList)}</td>
              <td style={ST.td}>{fmtMaybe(h.matchScore)}</td>
              <td style={ST.td}>{fmtMaybe(h.message)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// Jessica Beucler 2026-11-02: a CIP green light (name, address, date of birth, SSN; DL number when used for a temp ID).
// Same rules as the Identity Verification PDF (cipVerification mirrors CipVerification.cs).
function CipSection(props: { extract: IdentityProviderExtractRow; verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow> }): JSX.Element {
  const elements = cipVerification(props.extract, props.verifDetails);
  const ok = cipAllVerified(elements);
  const color = (r: string): string => (r === 'verified' ? BRAND.green : r === 'not-verified' ? BRAND.red : BRAND.amber);
  const label = (r: string): string =>
    r === 'verified' ? '✓ Verified' : r === 'partial' ? 'Partly verified' : r === 'not-verified' ? '✗ Not verified' : r === 'not-available' ? 'Not verified by LexisNexis' : 'Not checked';
  return (
    <div style={{ padding: '8px 12px', borderLeft: `4px solid ${ok ? BRAND.green : BRAND.amber}`, background: ok ? BRAND.bgSuccess : 'var(--warn-bg)', borderRadius: 4 }}>
      <div style={{ fontWeight: 700, color: ok ? BRAND.green : BRAND.amber, fontSize: 14, marginBottom: 4 }}>
        {ok ? (elements.some((e) => e.element === 'Driver license number')
              ? '✓ CIP: name, address, date of birth, SSN and driver’s license verified by LexisNexis'
              : '✓ CIP: name, address, date of birth and SSN verified by LexisNexis')
            : 'CIP: not every element verified by LexisNexis. Verify the rest from the customer’s documents'}
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
        <tbody>
          {elements.map((e) => (
            <tr key={e.element}>
              <th style={{ textAlign: 'left', padding: '1px 10px 1px 0', verticalAlign: 'top' }}>{e.element}</th>
              <td style={{ padding: '1px 10px 1px 0', whiteSpace: 'nowrap', fontWeight: 600, color: color(e.result), verticalAlign: 'top' }}>{label(e.result)}</td>
              <td style={{ padding: '1px 0', verticalAlign: 'top' }}>{e.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// CHG-0141 (Jessica via Reid 10/30): "if the customer later returns with supporting documents, a note of what was
// provided." Listed under the CIP box with the elements LexisNexis could not verify; officers add a note, never edit
// one. The note prints on the Identity Verification report (and a fresh copy is filed if the report already was).
function SupportingDocumentsSection(props: {
  extract: IdentityProviderExtractRow;
  verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow>;
}): JSX.Element {
  const api = useApiClient();
  const extractId = props.extract.id;
  const unverified = cipVerification(props.extract, props.verifDetails)
    .filter((e) => e.result !== 'verified')
    .map((e) => e.element);
  const [notes, setNotes] = useState<ReadonlyArray<IdentitySupportingDocumentRow>>([]);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<ReadonlyArray<string>>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!extractId) return;
    try {
      const r = await api.retail.identity.listSupportingDocuments(extractId);
      setNotes(r.notes ?? []);
    } catch {
      // Listing is informational; the add form still works.
    }
  }, [api, extractId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (el: string): void =>
    setPicked((cur) => (cur.includes(el) ? cur.filter((x) => x !== el) : [...cur, el]));

  const save = async (): Promise<void> => {
    if (!extractId || text.trim().length === 0) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await api.retail.identity.addSupportingDocument(extractId, {
        documentsProvided: text.trim(),
        ...(picked.length > 0 ? { elements: picked } : {}),
      });
      setText('');
      setPicked([]);
      setMessage({
        tone: 'ok',
        text: r.refiled
          ? 'Note recorded. A fresh copy of the Identity Verification report was filed with the application.'
          : 'Note recorded. It prints on the Identity Verification report.',
      });
      await load();
    } catch (err: unknown) {
      setMessage({ tone: 'err', text: (err as Error)?.message ?? 'The note was not recorded.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...ST.section, border: `1px solid ${BRAND.border}`, borderRadius: 4, padding: '8px 12px' }}>
      <h3 style={ST.h3}>Supporting documents provided by the customer</h3>
      {notes.length === 0 ? (
        <div style={{ color: BRAND.caption, fontSize: 12, marginBottom: 6 }}>None recorded.</div>
      ) : (
        <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 12 }}>
          {notes.map((n) => (
            <li key={n.supportingDocumentId}>
              {n.documentsProvided}
              {n.elements ? <span style={{ color: BRAND.caption }}> · For: {n.elements.split(',').join(', ')}</span> : null}
              <span style={{ color: BRAND.caption }}>
                {' '}· {n.recordedByName ?? n.recordedByUpn}, {new Date(n.recordedAt).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
      <label htmlFor="ln-supporting-docs" style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        What did the customer provide?
      </label>
      <textarea
        id="ln-supporting-docs"
        value={text}
        maxLength={2000}
        rows={2}
        onChange={(e) => setText(e.currentTarget.value)}
        placeholder="For example: utility bill dated 10/15 showing the home address"
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: 6 }}
      />
      {unverified.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, margin: '6px 0' }}>
          <span style={{ color: BRAND.caption }}>Supports:</span>
          {unverified.map((el) => (
            <label key={el} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={picked.includes(el)} onChange={() => toggle(el)} />
              {el}
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button variant="ghost" onClick={() => void save()} disabled={busy || text.trim().length === 0}>
          {busy ? 'Recording…' : 'Record note'}
        </Button>
        {message && (
          <span style={{ fontSize: 12, color: message.tone === 'ok' ? BRAND.green : BRAND.red }}>{message.text}</span>
        )}
      </div>
    </section>
  );
}

// Ginger McCullough 2026-11-02: discrepancies first, shaded, with the follow-up actions, so the FSR sees the risks.
// Same list as the Identity Verification PDF (lnDiscrepancies mirrors LnReportHtmlBuilder.Discrepancies).
function DiscrepanciesSection(props: {
  extract: IdentityProviderExtractRow;
  verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow>;
  redFlags: ReadonlyArray<IdentityProviderRedFlagRow>;
  fraudWarnings: ReadonlyArray<IdentityProviderFraudWarningRow>;
}): JSX.Element {
  const { config } = useConfig();
  const settings = config.systemSettings as Record<string, unknown> | undefined;
  const review = fraudIndexReview(settings);
  const items = lnDiscrepancies(props.extract, props.verifDetails, props.redFlags, props.fraudWarnings, review);
  const available = availableFollowUps(settings);
  const actions = tryParseJsonArray<string>(props.extract.followUpActionsJson)
    .filter((a) => { const tool = followUpToolOf(a); return tool === null || available.has(tool); });
  if (items.length === 0) {
    return (
      <div style={{ padding: '8px 12px', borderLeft: `4px solid ${BRAND.green}`, background: BRAND.bgSuccess, color: BRAND.green, borderRadius: 4 }}>
        <strong>No discrepancies:</strong> every detail LexisNexis checked verified, and no risk indicators, red flags or fraud warnings were returned.
      </div>
    );
  }
  return (
    <div role="alert" style={{ padding: '8px 12px', borderLeft: `4px solid ${BRAND.red}`, background: BRAND.bgError, borderRadius: 4 }}>
      <div style={{ fontWeight: 700, color: BRAND.red, fontSize: 14, marginBottom: 4 }}>Discrepancies and follow-up</div>
      <ul style={{ margin: '0 0 6px', paddingLeft: 20 }}>
        {items.map((i, n) => <li key={n} style={{ marginBottom: 2 }}>{i}</li>)}
      </ul>
      {actions.length > 0 && (
        <>
          <div style={{ fontWeight: 700, color: BRAND.dark }}>Potential follow-up actions</div>
          <ul style={{ margin: '2px 0 4px', paddingLeft: 20 }}>
            {actions.map((a, n) => <li key={n}>{a}</li>)}
          </ul>
        </>
      )}
      <div style={{ fontSize: 11, color: BRAND.body }}>
        Compare each item with the applicant and their documents before opening.
      </div>
    </div>
  );
}

function VerificationDetailSection({ rows }: { rows: ReadonlyArray<IdentityProviderVerifDetailRow> }): JSX.Element | null {
  if (rows.length === 0) return null;
  const statusPill = (s: string): JSX.Element => {
    if (s === 'matched')    return <span style={pillStyle(BRAND.bgSuccess, BRAND.green)}>matched</span>;
    if (s === 'mismatched') return <span style={pillStyle(BRAND.bgError,   BRAND.red)}>✗ did not verify</span>;
    return <span style={pillStyle(BRAND.bgCode, BRAND.caption)}>n/a</span>;
  };
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Verification detail ({rows.length} fields)</h3>
      <table style={ST.table}>
        <thead>
          <tr>
            <th style={ST.th}>Field</th>
            <th style={ST.th}>Submitted</th>
            <th style={ST.th}>Verified</th>
            <th style={ST.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={r.matchStatus === 'mismatched' ? { background: BRAND.bgError, color: BRAND.red, fontWeight: 600 } : undefined}>
              <td style={{ ...ST.td, fontWeight: r.matchStatus === 'mismatched' ? 700 : 500 }}>{r.fieldName}</td>
              <td style={ST.td}>{fmtMaybe(r.inputValue)}</td>
              <td style={ST.td}>{fmtMaybe(r.verifiedValue)}</td>
              <td style={{ ...ST.td, whiteSpace: 'nowrap' }}>{statusPill(r.matchStatus)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RedFlagsSection({ rows }: { rows: ReadonlyArray<IdentityProviderRedFlagRow> }): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Red flags ({rows.length})</h3>
      {rows.map((r) => {
        const alerts = tryParseJsonArray<string>(r.alertsJson);
        return (
          <div key={r.id} style={{ marginBottom: 10, padding: 10, background: BRAND.bgError, borderLeft: `4px solid ${BRAND.red}`, borderRadius: 4 }}>
            <div style={{ fontWeight: 600, color: BRAND.red, marginBottom: 4 }}>{r.category}</div>
            {alerts.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: BRAND.body }}>
                {alerts.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}

function FraudWarningsSection({ rows }: { rows: ReadonlyArray<IdentityProviderFraudWarningRow> }): JSX.Element | null {
  if (rows.length === 0) return null;
  const priorityColor = (p: number): { bg: string; fg: string } => {
    if (p >= 5) return { bg: BRAND.bgError,   fg: BRAND.red };
    if (p >= 3) return { bg: BRAND.bgWarn,    fg: 'var(--warn-text)' };
    return       { bg: BRAND.bgCode,    fg: BRAND.caption };
  };
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Fraud warnings ({rows.length})</h3>
      <table style={ST.table}>
        <thead>
          <tr>
            <th style={ST.th}>Priority</th>
            <th style={ST.th}>Field</th>
            <th style={ST.th}>Value</th>
            <th style={ST.th}>Warning</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const c = priorityColor(r.priorityCode);
            return (
              <tr key={r.id}>
                <td style={{ ...ST.td, whiteSpace: 'nowrap' }}>
                  <span style={pillStyle(c.bg, c.fg)}>{r.priorityCode}</span>
                </td>
                <td style={{ ...ST.td, fontWeight: 500 }}>{r.inputField}</td>
                <td style={ST.td}>{fmtMaybe(r.inputValue)}</td>
                <td style={ST.td}>{r.warningText}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function SupplementalSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element | null {
  // Render only if any supplemental field is populated. Right now the
  // IdentityProviderExtractRow DTO doesn't expose the full supplemental
  // surface (LexID is the one consistently surfaced). When the DTO grows,
  // add rows here without changing the parent.
  const hasAny = extract.lexIdFound;
  if (!hasAny) return null;
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Supplemental data</h3>
      <div style={ST.kv}>
        <div style={ST.kvLabel}>LexID</div>
        <div style={ST.kvValue}>{fmtMaybe(extract.lexIdFound)}</div>
      </div>
    </section>
  );
}

function IpAddressSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element | null {
  // IP-geo fields aren't on the lean ExtractRow DTO yet; placeholder for
  // when api-client surfaces them. Kept here to make the future wire-in
  // a one-line component-fill rather than a structural change.
  return null;
}

// D4 (Bill 2026-06-03) — surface the LexisNexis follow-up actions.
// PRIMARY: the report's OWN parsed follow-up actions (followUpActionsJson, e.g.
// "Verify name with Address via DL / utility bill / …"). These are the specific,
// report-driven recommendations LN printed — surface them verbatim.
// FALLBACK: only when the report carried no parsed actions do we derive generic
// LN-platform tool suggestions from the signals (risk indicators / red flags /
// weak verification). Both are advisory, NOT procedure-gating (FraudPoint is the
// gate), so no Core-Director-destination requirement.
function FollowUpSection({
  extract,
  redFlags,
  fraudWarnings,
}: {
  extract: IdentityProviderExtractRow;
  redFlags: ReadonlyArray<IdentityProviderRedFlagRow>;
  fraudWarnings: ReadonlyArray<IdentityProviderFraudWarningRow>;
}): JSX.Element | null {
  const { config } = useConfig();
  // Only tools PCB can actually run (Risk Management setting; Bill 2026-10-06 - KBA and
  // SmartLinx are not in PCB's LexisNexis contract, so they are never suggested by default).
  const available = availableFollowUps(config.systemSettings as Record<string, unknown> | undefined);
  const runnable = (text: string): boolean => {
    const tool = followUpToolOf(text);
    return tool === null || available.has(tool);
  };
  // PRIMARY — the report's own parsed follow-up actions win when present.
  const parsedActions = tryParseJsonArray<string>(extract.followUpActionsJson).filter(runnable);
  if (parsedActions.length > 0) {
    return (
      <section style={ST.section}>
        <h3 style={ST.h3}>Suggested follow-up actions</h3>
        <ul style={{ margin: '0 0 6px 0', paddingLeft: 22, color: BRAND.body }}>
          {parsedActions.map((a, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{a}</li>
          ))}
        </ul>
        <p style={{ margin: 0, fontSize: 11, color: BRAND.caption, fontStyle: 'italic' }}>
          LexisNexis-recommended for this report — advisory context only; results inform but do not gate the opening (FraudPoint score is the gate).
        </p>
      </section>
    );
  }

  // FALLBACK — derive generic LN-platform tool suggestions from the signals.
  const riskIndicators = tryParseJsonArray<string>(extract.potentialRiskIndicatorsJson);
  const hasConcerns = redFlags.length > 0 || fraudWarnings.length > 0 || riskIndicators.length > 0;

  // Weak verification summary → corroborate identity / contactability.
  const summaryText = `${extract.verifSummary_NameAddrPhone ?? ''} ${extract.verifSummary_NameAddrSsn ?? ''}`.toLowerCase();
  const weakVerif = /no match|partial|not? ?verif|unable/.test(summaryText);

  const allTools: Array<{ tool: LnFollowUpTool; name: string; why: string; suggested: boolean }> = [
    { tool: 'kba', name: 'InstantID Q&A (KBA)', why: 'Knowledge-based challenge to corroborate identity when red flags or risk indicators are present.', suggested: hasConcerns },
    { tool: 'smartlinx', name: 'Find-a-Person / SmartLinx', why: 'Resolve associated identities/addresses when the application data doesn’t fully verify.', suggested: hasConcerns || weakVerif },
    { tool: 'phoneAddress', name: 'Phone / Address lookup', why: 'Confirm contactability when Name+Address+Phone didn’t verify.', suggested: weakVerif },
    { tool: 'location', name: 'Location lookup', why: 'Corroborate current residence when the address is unverified.', suggested: weakVerif },
  ];
  const tools = allTools.filter((t) => available.has(t.tool));
  if (tools.length === 0) return null;
  const suggested = tools.filter((t) => t.suggested);
  const others = tools.filter((t) => !t.suggested);

  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Suggested follow-up actions</h3>
      {suggested.length > 0 ? (
        <ul style={{ margin: '0 0 6px 0', paddingLeft: 22, color: BRAND.body }}>
          {suggested.map((t) => (
            <li key={t.name} style={{ marginBottom: 4 }}>
              <strong>{t.name}</strong> — <span style={{ color: BRAND.caption }}>{t.why}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ margin: '0 0 6px 0', fontSize: 12, color: BRAND.caption }}>
          No specific follow-up indicated by this report. The tools below remain available.
        </p>
      )}
      {others.length > 0 && (
        <p style={{ margin: 0, fontSize: 11, color: BRAND.caption }}>
          Also available: {others.map((t) => t.name).join(' · ')}.
        </p>
      )}
      <p style={{ margin: '6px 0 0 0', fontSize: 11, color: BRAND.caption, fontStyle: 'italic' }}>
        Advisory only — run any relevant tool in the LexisNexis platform; results inform but do not gate the opening (FraudPoint score is the gate).
      </p>
    </section>
  );
}

function PermissibleUseSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element | null {
  return null; // PermissibleUse_* not on the lean DTO; future surface.
}

function AuditSection({ extract }: { extract: IdentityProviderExtractRow }): JSX.Element {
  return (
    <section style={ST.section}>
      <h3 style={ST.h3}>Audit</h3>
      <div style={ST.kv}>
        <div style={ST.kvLabel}>Extract ID</div>
        <div style={ST.kvValue}>{extract.id}</div>
        <div style={ST.kvLabel}>Inquiry ID</div>
        <div style={ST.kvValue}>{extract.inquiryId}</div>
        {extract.signalId != null && <>
          <div style={ST.kvLabel}>Signal ID</div>
          <div style={ST.kvValue}>{extract.signalId}</div>
        </>}
      </div>
    </section>
  );
}
